import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { history } from 'nallian-shared-ui/lib/config/history';
import updatePrincipalService from '../../services/principals/updatePrincipalService';
import principalService from '../../services/principals/principalService';
import PrincipalForm from './PrincipalForm';
import { TABS } from '../../constants';
import { mapPrincipalCommunityApps } from '../../helpers/principals/mapPrincipalCommunityApps';

const EditPrincipal = ({
  open,
  dispatchUpdatePrincipalPut,
  updatePrincipalFetch,
  dispatchPrincipalGet,
  principalFetch,
  organisationId,
  urlRoot,
  isSystemOrg,
  forbidManageBecauseSystemOrg,
}) => {
  const { actionId } = useParams();
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    body => {
      dispatchUpdatePrincipalPut(
        actionId,
        {
          ...body,
          organisation: { id: organisationId },
          communityApps: mapPrincipalCommunityApps(body.communityApps),
        },
        () => history.push(`${urlRoot}/${TABS.PRINCIPALS}`),
        t
      );
    },
    [urlRoot, actionId, organisationId, dispatchUpdatePrincipalPut, t]
  );

  useEffect(() => {
    if (open && actionId) {
      dispatchPrincipalGet(actionId, t);
    }
  }, [actionId, open, t, dispatchPrincipalGet]);

  const defaultData = useMemo(
    () =>
      actionId === principalFetch.value?.id &&
      !principalFetch.pending && {
        ...(principalFetch.value || {}),
        primaryPhoneNumber: principalFetch.value?.primaryPhoneNumber || '',
        communityApps: (principalFetch.value?.communityApps || []).map(ca => ({
          app: `${ca.appInstallation?.tag}_${ca.community?.tag}`,
          features: (ca.communityAppFeatures || []).map(caf => ({
            feature: caf.appInstallationFeature?.tag,
            roles: (caf.communityAppFeatureRoles || []).map(r => r.appInstallationFeatureRole?.tag),
          })),
        })),
        inviteRequest: {
          invitationType: 1, // temporary fixed (1 email), will later come from api
        },
      },
    [principalFetch, actionId]
  );

  if (!open) return null;

  return (
    <PrincipalForm
      header={
        <span>
          {t('editUser')}&nbsp;-&nbsp;{principalFetch.value?.fullName}
        </span>
      }
      onSubmit={handleSubmit}
      submitProcessing={updatePrincipalFetch.pending}
      returnRoute={`${urlRoot}/${TABS.PRINCIPALS}`}
      cancelRoute={`${urlRoot}/${TABS.PRINCIPALS}`}
      dataLoading={principalFetch.pending}
      data={!principalFetch.pending && defaultData}
      forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
      organisationId={organisationId}
      isSystemOrg={isSystemOrg}
      showRemoveButton
      editMode
    />
  );
};

EditPrincipal.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  dispatchUpdatePrincipalPut: PropTypes.func.isRequired,
  updatePrincipalFetch: PropTypes.object.isRequired,
  dispatchPrincipalGet: PropTypes.func.isRequired,
  principalFetch: PropTypes.object.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
  isSystemOrg: PropTypes.bool.isRequired,
  open: PropTypes.bool,
};

EditPrincipal.defaultProps = { open: false };

export default compose(principalService, updatePrincipalService)(EditPrincipal);
